import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';

import Breadcrumbs from '../../components/ui/Breadcrumbs';
import Tabs from '../../components/ui/Tabs';
import AsyncGenericSelect from '../../components/ui/AsyncGenericSelect';
import GenericSelect from '../../components/ui/GenericSelect';
import CheckboxInput from '../../components/ui/CheckboxInput';
import Button from '../../components/ui/Button';
import PmScopeFilterBar, { Field } from '../../components/performance/PmScopeFilterBar';
import usePmAnalyticsFilters, { EMPTY_FILTERS, serializeFilters } from '../../components/performance/pmAnalyticsFilters';
import { useAuth } from '../../context/AuthContext';
import { fetchPeriods, usePanel } from '../performance-operations/config';
import { Grid, PageEmpty } from '../performance-operations/styles';
import TrendKpis from './components/TrendKpis';
import TrendSeriesPanel from './components/TrendSeriesPanel';
import TransitionPanel from './components/TransitionPanel';
import UnitTrendPanel from './components/UnitTrendPanel';
import TrendInsightsPanel from './components/TrendInsightsPanel';
import TrendUnitsTab from './components/TrendUnitsTab';
import TrendEmployeesTab from './components/TrendEmployeesTab';
import ScaleMappingDrawer from './components/ScaleMappingDrawer';
import {
  MAX_PERIODS, MIN_PERIODS, RESULTS_PATH, chronological, defaultTrendPeriods, fetchSeries, fetchSummary, fetchTransitions,
  fetchUnits, parseTrendState, serializeTrendState, trendParams,
} from './config';

// PM analitik P6 — E11 Dönemler arası trendler (/performance/trends; analiz §14). İK ve astı olan yöneticiler (kapsam sunucuda:
// İK kiracı, yönetici güncel alt ağacı). Yerli ve içe aktarılmış (geçmiş) dönemler aynı seride. Süzgeçler ve seçimler URL'dedir
// (geri dönüşte korunur); her panel kendi isteğini atar ve kendi hata/iskelet durumunu taşır (usePanel). Tek kaydırma: Özet
// sekmesi kendi alanında kayar, liste sekmelerinde yalnız DataTable satırları kayar.
const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  overflow: hidden;
`;

const Header = styled.div`
  flex-shrink: 0;
`;

const TabContent = styled.div`
  flex: 1;
  min-height: 0;
  overflow: hidden;
`;

const ScrollArea = styled.div`
  height: 100%;
  min-height: 0;
  overflow-y: auto;
  padding-bottom: 16px;
`;

export default function PerformanceTrendsPage() {
  const { t } = useTranslation();
  const location = useLocation();
  const history = useHistory();
  // İK rolü ayrımı — İK = sunucunun kararı (AuthContext.isHr), Şirket Yöneticisi DEĞİL.
  const { isHr } = useAuth();
  const { filters, setFilters } = usePmAnalyticsFilters();
  const trend = useMemo(() => parseTrendState(location.search), [location.search]);
  const setTrend = useCallback((patch) => {
    history.replace({ pathname: location.pathname, search: serializeTrendState({ ...trend, ...patch }, location.search) });
  }, [history, location.pathname, location.search, trend]);

  const [periods, setPeriods] = useState([]);
  const [periodsLoaded, setPeriodsLoaded] = useState(false);
  const [scaleDrawer, setScaleDrawer] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [tabHeight, setTabHeight] = useState(0);
  const tabRef = useRef(null);

  useEffect(() => {
    let alive = true;
    fetchPeriods().then((list) => { if (alive) { setPeriods(list); setPeriodsLoaded(true); } });
    return () => { alive = false; };
  }, []);

  // Seçim boşsa ya da artık geçerli değilse varsayılan: en son kapanmış 3 dönem (yerli ya da içe aktarılmış).
  useEffect(() => {
    if (!periodsLoaded || periods.length < MIN_PERIODS) return;
    const valid = trend.periods.filter((id) => periods.some((p) => p.id === id));
    if (valid.length < MIN_PERIODS) setTrend({ periods: defaultTrendPeriods(periods, valid) });
    else if (valid.length !== trend.periods.length) setTrend({ periods: valid });
  }, [periodsLoaded, periods, trend.periods, setTrend]);

  useEffect(() => {
    const el = tabRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver((entries) => entries.forEach((e) => setTabHeight(e.contentRect.height)));
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const selected = useMemo(() => chronological(periods.filter((p) => trend.periods.includes(p.id))), [periods, trend.periods]);
  const ready = selected.length >= MIN_PERIODS;
  const params = useMemo(() => trendParams(filters, { ...trend, periods: selected.map((p) => p.id) }), [filters, trend, selected]);
  const key = `${JSON.stringify(params)}|${refreshKey}`;
  const summary = usePanel(() => fetchSummary(params), [key], ready && trend.tab === 'summary');
  const series = usePanel(() => fetchSeries(params), [key], ready && trend.tab === 'summary');
  const transitions = usePanel(() => fetchTransitions(params), [key], ready && trend.tab === 'summary');
  const units = usePanel(() => fetchUnits(params), [key], ready && (trend.tab === 'summary' || trend.tab === 'units'));

  const periodLookup = useMemo(() => ({
    onSearch: async (q) => periods
      .filter((p) => !q || String(p.name || '').toLocaleLowerCase().includes(String(q).toLocaleLowerCase()))
      .map((p) => ({ id: p.id, name: p.origin === 'imported' ? `${p.name} · ${t('performance.trends.imported')}` : p.name })),
    onGetById: async (id) => {
      const p = periods.find((x) => x.id === id);
      return { id, name: p ? p.name : '' };
    },
  }), [periods, t]);

  const onPeriodsChange = (ids) => {
    const next = (ids || []).slice(0, MAX_PERIODS);
    setTrend({ periods: next, fromBand: null, toBand: null });
  };
  const goEmployees = (fromBand, toBand) => setTrend({ tab: 'employees', fromBand, toBand });
  // "Sıfırla" rolün varsayılanına döner: dönem seçimi korunur, F0 süzgeçleri + mod + aynı rol + geçiş hücresi tek yazımda kalkar.
  const onReset = () => {
    const base = serializeTrendState({ ...trend, mode: 'cohort', sameRole: false, fromBand: null, toBand: null }, location.search);
    history.replace({ pathname: location.pathname, search: serializeFilters({ ...EMPTY_FILTERS }, base) });
  };
  const onPoint = (periodId) => {
    if (RESULTS_PATH) history.push(`${RESULTS_PATH}?period=${periodId}`);
    else setTrend({ tab: 'employees', fromBand: null, toBand: null });
  };
  const mismatch = series.data?.scaleComparison && !series.data.scaleComparison.comparable
    ? series.data.scaleComparison.missingPairs[0] : null;

  const extraChips = trend.fromBand && trend.toBand ? [{
    key: 'transition',
    label: t('performance.trends.transition.chip', { from: t(`performance.trends.band.${trend.fromBand}`), to: t(`performance.trends.band.${trend.toBand}`) }),
    onRemove: () => setTrend({ fromBand: null, toBand: null }),
  }] : [];

  const tabs = [
    { key: 'summary', label: t('performance.trends.tab.summary') },
    { key: 'units', label: t('performance.trends.tab.units') },
    { key: 'employees', label: t('performance.trends.tab.employees') },
  ];

  return (
    <PageContainer>
      <Header>
        <Breadcrumbs items={[{ label: t('performance.trends.title') }]} />
        {periodsLoaded && periods.length < MIN_PERIODS ? <PageEmpty data-state="no-periods">{t('performance.trends.noPeriods')}</PageEmpty> : null}
        {periods.length >= MIN_PERIODS ? (
          <PmScopeFilterBar
            filters={filters}
            onChange={setFilters}
            onReset={onReset}
            extraChips={extraChips}
            lastUpdated={summary.data?.calculatedAt}
            periodControl={(
              <Field $w="320px">
                <AsyncGenericSelect
                  label={t('performance.trends.control.periods')}
                  value={trend.periods}
                  onChange={onPeriodsChange}
                  isMulti
                  minChars={0}
                  compact
                  {...periodLookup}
                />
              </Field>
            )}
            extraControls={(
              <>
                <Field $w="200px">
                  <GenericSelect
                    label={t('performance.trends.control.mode')}
                    value={trend.mode}
                    onChange={(v) => setTrend({ mode: v || 'cohort' })}
                    options={[{ id: 'cohort', name: t('performance.trends.mode.cohort') }, { id: 'population', name: t('performance.trends.mode.population') }]}
                    idKey="id"
                    labelKey="name"
                    clearable={false}
                  />
                </Field>
                <CheckboxInput
                  label={t('performance.trends.control.sameRole')}
                  checked={trend.sameRole}
                  onChange={(v) => setTrend({ sameRole: !!v })}
                />
                {isHr ? (
                  <Button variant="outline" onClick={() => setScaleDrawer(true)} data-action="scale-mapping">
                    {t('performance.trends.scaleMapping.open')}
                  </Button>
                ) : null}
              </>
            )}
          />
        ) : null}
        {periods.length >= MIN_PERIODS && !ready ? <PageEmpty data-state="select-periods">{t('performance.trends.selectPeriods', { min: MIN_PERIODS, max: MAX_PERIODS })}</PageEmpty> : null}
        {ready ? <Tabs tabs={tabs} activeTab={trend.tab} onChange={(tab) => setTrend({ tab })} /> : null}
      </Header>
      <TabContent ref={tabRef}>
        {ready && trend.tab === 'summary' ? (
          <ScrollArea>
            <Grid>
              <TrendKpis panel={summary} />
              <TrendSeriesPanel panel={series} onPoint={onPoint} canManageScales={isHr} onScaleMapping={() => setScaleDrawer(true)} />
              <TransitionPanel panel={transitions} onCell={goEmployees} />
              <UnitTrendPanel panel={units} summary={summary.data} onSeeAll={() => setTrend({ tab: 'units' })} />
              <TrendInsightsPanel panel={summary} />
            </Grid>
          </ScrollArea>
        ) : null}
        {ready && trend.tab === 'units' ? <TrendUnitsTab panel={units} periods={selected} height={tabHeight} /> : null}
        {ready && trend.tab === 'employees' ? (
          <TrendEmployeesTab params={params} periods={selected} fromBand={trend.fromBand} toBand={trend.toBand} height={tabHeight} />
        ) : null}
      </TabContent>
      {isHr ? (
        <ScaleMappingDrawer
          open={scaleDrawer}
          onClose={() => setScaleDrawer(false)}
          onChanged={() => setRefreshKey((k) => k + 1)}
          suggested={mismatch}
        />
      ) : null}
    </PageContainer>
  );
}
